/* Холд и выдачи зарплаты (16.10.2026, владелец) — запросы к /api/payouts (apps/api/src/routes/payouts.ts).
   Холд = заработано за закрытые недели (пт–чт) − выдано: «+» мы должны, «−» должен нам. Выдача — с одноразовым
   ключом (request_id): повтор того же нажатия (двойной тап, обрыв связи) сервер не запишет второй раз. */
import { api } from "../api/client";
import { ApiException } from "../api/types";

/** opening — стартовый холд на 09.10 (16.10, владелец: «даю стартовую точку»), входит в hold. */
export interface HoldRow { staff_id: string; name: string; opening: number; earned: number; paid: number; hold: number; current_week: number }
export interface Holds { start: string; closed: { from: string; to: string } | null; current: { from: string; to: string }; rows: HoldRow[] }
export interface Payout {
  id: number; staff_id: string; staff_name?: string | null; amount: number; paid_at: string; comment: string; created_by_name: string;
  hold_before: number | null; cancelled_at: string | null; cancelled_by_name: string | null; cancel_reason: string | null;
}
export interface Week { from: string; to: string; earned: number }
export interface Opening { amount: number; as_of: string; note: string; set_by_name: string; set_at: string }

export const fetchHolds = () => api<Holds>("/api/payouts/holds");
export const fetchPayouts = (staffId?: string) =>
  api<{ rows: Payout[] }>(`/api/payouts${staffId ? `?staff_id=${encodeURIComponent(staffId)}` : "?limit=200"}`).then((r) => r.rows ?? []);
export const fetchWeeks = (staffId: string) => api<{ weeks: Week[]; current: Week; opening: Opening | null }>(`/api/payouts/weeks?staff_id=${encodeURIComponent(staffId)}`);
export const putOpening = (staffId: string, amount: number) =>
  api<{ ok: true; hold_after: number }>("/api/payouts/opening", { method: "PUT", body: { staff_id: staffId, amount }, retries: 0 });
export const cancelPayout = (id: number, reason = "") =>
  api<{ ok: boolean; already?: boolean; hold_after: number }>(`/api/payouts/${id}/cancel`, { method: "POST", body: reason ? { reason } : {}, retries: 0 });

/** Ключ одной выдачи: заводится при открытии окна и живёт до успешной записи. */
export function newRequestId(): string {
  try { if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID(); } catch { /* старый WebView */ }
  return `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export interface PayInput { staff_id: string; amount: number; expected_hold: number; request_id: string; day?: string; comment: string; confirm_overpay?: boolean }
export interface Recent { amount: number; created_at: string; created_by_name: string }
export type PayResult =
  | { ok: true; payout: Payout; holdAfter: number; duplicate: boolean }
  | { ok: false; code: "hold_changed"; hold: number; recent: Recent[]; error: string }
  | { ok: false; code: "overpay"; after: number; error: string }
  | { ok: false; code: "cancelled" | "request_reused" | "other"; error: string }
  /** исход неизвестен (обрыв, таймаут, 5xx): ключ держим — повтор безопасен */
  | { ok: false; code: "unknown"; error: string };

const UNKNOWN = "Не знаем, записалась ли выдача (связь или сервер не ответили). Нажмите ещё раз — повтор безопасен, дважды не запишется";
export async function postPayout(input: PayInput): Promise<PayResult> {
  const ac = new AbortController();
  const t = window.setTimeout(() => ac.abort(), 25_000);   // зависший запрос не запирает окно навсегда
  try {
    const r = await api<{ ok: true; payout: Payout; hold_after: number; duplicate?: boolean }>("/api/payouts", { method: "POST", body: input, retries: 0, signal: ac.signal });
    return { ok: true, payout: r.payout, holdAfter: r.hold_after, duplicate: !!r.duplicate };
  } catch (e) {
    if (e instanceof ApiException && e.status < 500 && e.body) {
      const b = e.body as { code?: string; error?: string; hold?: number; after?: number; recent?: Recent[] };
      const error = b.error || `Ошибка ${e.status}`;
      if (e.status === 409 && b.code === "hold_changed" && typeof b.hold === "number") return { ok: false, code: "hold_changed", hold: b.hold, recent: b.recent ?? [], error };
      if (e.status === 409 && b.code === "overpay" && typeof b.after === "number") return { ok: false, code: "overpay", after: b.after, error };
      if (e.status === 409 && (b.code === "cancelled" || b.code === "request_reused")) return { ok: false, code: b.code, error };
      return { ok: false, code: "other", error };
    }
    return { ok: false, code: "unknown", error: UNKNOWN };   // 5xx уже после записи, обрыв, таймаут
  } finally { window.clearTimeout(t); }
}

/** ₽ с пробелами; signed — с «+»/«−» (минус — U+2212, как везде в зарплатах). */
export const rub = (v: number) => `₽${Math.abs(Math.round(v)).toLocaleString("ru-RU")}`;
export const signedRub = (v: number) => (v > 0 ? `+${rub(v)}` : v < 0 ? `−${rub(v)}` : "0");
export const holdTone = (v: number): "owe" | "debt" | "zero" => (v > 0 ? "owe" : v < 0 ? "debt" : "zero");
export const holdNote = (v: number) => (v > 0 ? "мы должны" : v < 0 ? "должен нам" : "в расчёте");
const dm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
export const weekLabel = (w: { from: string; to: string }) => `${dm(w.from)}–${dm(w.to)}`;
/** paid_at/cancelled_at — UTC «YYYY-MM-DD HH:MM:SS» → «16.10 14:05» по Оренбургу. */
export function orenStamp(utc: string | null | undefined): string {
  const t = utc ? Date.parse(`${utc.replace(" ", "T")}Z`) : NaN;
  if (!Number.isFinite(t)) return "";
  const s = new Date(t + 5 * 3_600_000).toISOString();
  return `${s.slice(8, 10)}.${s.slice(5, 7)} ${s.slice(11, 16)}`;
}
